import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from "react";
import { getApiBase, getAuthHeaders } from "@/lib/api";
import { useLocation } from "wouter";
import { PlateTalkRequestError, plateTalkErrorMessage } from "./plate-talk-errors";

interface PlateTalkAction {
  action: string;
  [key: string]: any;
}

export interface PlateTalkMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  image?: string;
  actions?: PlateTalkAction[];
  quickSuggestions?: string[];
  timestamp: Date;
  streaming?: boolean;
  statusMessage?: string;
}

type PlateTalkState = "idle" | "thinking" | "streaming";

interface QueuedRequest {
  text: string;
  image?: string | null;
  conversationId?: number | null;
  voiceEnabled?: boolean;
  speakFn?: (t: string) => void;
}

interface PlateTalkStoreValue {
  messages: PlateTalkMessage[];
  setMessages: React.Dispatch<React.SetStateAction<PlateTalkMessage[]>>;
  state: PlateTalkState;
  activeConversationId: number | null;
  setActiveConversationId: (id: number | null) => void;
  sendMessage: (text: string, image?: string | null, conversationId?: number | null, voiceEnabled?: boolean, speakFn?: (t: string) => void) => void;
  abort: () => void;
  completedWhileAway: boolean;
  clearCompletedFlag: () => void;
  lastError: string | null;
  queueLength: number;
}

const PlateTalkContext = createContext<PlateTalkStoreValue | null>(null);

// Some transports do not settle a pending read promptly when aborted. Stop
// consuming immediately, even if transport cleanup itself is still pending.
function untilAborted<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException("Aborted", "AbortError"));
    if (signal.aborted) { abort(); return; }
    signal.addEventListener("abort", abort, { once: true });
    work.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}

export function PlateTalkProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<PlateTalkMessage[]>([]);
  const [state, setState] = useState<PlateTalkState>("idle");
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [completedWhileAway, setCompletedWhileAway] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [queueLength, setQueueLength] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const onPageRef = useRef(true);
  const queueRef = useRef<QueuedRequest[]>([]);
  const processingRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const activeConversationIdRef = useRef<number | null>(activeConversationId);
  activeConversationIdRef.current = activeConversationId;
  const messagesRef = useRef<PlateTalkMessage[]>(messages);
  messagesRef.current = messages;

  const setOnPage = useCallback((on: boolean) => {
    onPageRef.current = on;
  }, []);

  const clearCompletedFlag = useCallback(() => {
    setCompletedWhileAway(false);
  }, []);

  const abort = useCallback(() => {
    queueRef.current = [];
    setQueueLength(0);
    abortRef.current?.abort();
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      queueRef.current = [];
      abortRef.current?.abort();
    };
  }, []);

  const processRequest = useCallback((req: QueuedRequest) => {
    const { text, image, conversationId, voiceEnabled, speakFn } = req;
    const requestId = ++requestSequenceRef.current;
    const msgId = `a-${requestId}`;
    const displayText = text.trim() || (image ? "Log this meal from photo" : "");

    const userMsg: PlateTalkMessage = {
      id: `u-${requestId}`,
      role: "user",
      content: displayText,
      image: image || undefined,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setState("thinking");
    setLastError(null);

    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
      let timedOut = false;
      const timeout = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 120_000);
      try {
        const base = getApiBase();
        const authHeaders = await untilAborted(getAuthHeaders(), controller.signal);
        if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
        const body: any = {
          message: displayText,
          conversationId: conversationId ?? activeConversationIdRef.current ?? undefined,
        };
        if (image) body.image = image;

        const response = await untilAborted(fetch(`${base}/plate-talk/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          credentials: "include",
          body: JSON.stringify(body),
          signal: controller.signal,
        }), controller.signal);
        if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");

        if (!response.ok || !response.body) {
          const errBody = await response.json().catch(() => null);
          const code = errBody?.code ?? errBody?.error;
          if (code === "ai_budget_exceeded") {
            window.dispatchEvent(new CustomEvent("ai-budget-exceeded", { detail: { resetsAt: errBody.resetsAt } }));
          }
          throw new PlateTalkRequestError(plateTalkErrorMessage(code, response.status));
        }

        const streamingMsg: PlateTalkMessage = {
          id: msgId,
          role: "assistant",
          content: "",
          actions: [],
          quickSuggestions: [],
          timestamp: new Date(),
          streaming: true,
        };
        setMessages(prev => [...prev, streamingMsg]);
        setState("streaming");

        reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let currentEvent = "";
        let accumulatedText = "";
        let finalized = false;
        const finalize = () => {
          if (finalized) return;
          finalized = true;
          setMessages(prev => prev.map(m => m.id === msgId ? { ...m, streaming: false, statusMessage: undefined } : m));
        };

        const processLine = (line: string) => {
          if (finalized || controller.signal.aborted || !mountedRef.current) return;
          line = line.replace(/\r$/, "");
          if (line.startsWith("event:")) {
            currentEvent = line.substring(6).trim();
          } else if (line.startsWith("data:")) {
            const dataStr = line.substring(5).trim();
            if (!currentEvent || !dataStr) return;

            try {
              const data = JSON.parse(dataStr);
              switch (currentEvent) {
                case "text":
                  accumulatedText += data.t || "";
                  setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content: accumulatedText } : m));
                  break;
                case "reset":
                  accumulatedText = "";
                  setMessages(prev => prev.map(m => m.id === msgId
                    ? { ...m, content: "", statusMessage: undefined }
                    : m));
                  break;
                case "conversationId":
                  if (data.id) {
                    activeConversationIdRef.current = data.id;
                    setActiveConversationId(data.id);
                  }
                  break;
                case "actions":
                  if (Array.isArray(data)) {
                    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, actions: data, statusMessage: undefined } : m));
                  }
                  break;
                case "suggestions":
                  if (Array.isArray(data)) {
                    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, quickSuggestions: data } : m));
                  }
                  break;
                case "done":
                  finalize();
                  // Speech is secondary: a synchronous failure must not keep
                  // the response open or cause a duplicate completion.
                  if (accumulatedText && voiceEnabled && speakFn) {
                    try { speakFn(accumulatedText); } catch { /* optional speech */ }
                  }
                  break;
                case "status":
                  setMessages(prev => prev.map(m => m.id === msgId ? { ...m, statusMessage: data.message || "" } : m));
                  break;
                case "error":
                  if (data.code === "ai_budget_exceeded") {
                    window.dispatchEvent(new CustomEvent("ai-budget-exceeded", { detail: { resetsAt: data.resetsAt } }));
                  }
                  setMessages(prev => prev.map(m => m.id === msgId ? {
                    ...m,
                    content: [m.content, plateTalkErrorMessage(data.code)].filter(Boolean).join("\n\n"),
                    streaming: false,
                    statusMessage: undefined,
                  } : m));
                  setLastError(plateTalkErrorMessage(data.code));
                  finalize();
                  break;
              }
            } catch {}
            currentEvent = "";
          } else if (line === "") {
            currentEvent = "";
          }
        };

        while (!finalized && !controller.signal.aborted) {
          const { done, value } = await untilAborted(reader.read(), controller.signal);
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          while (true) {
            const nlIdx = buffer.indexOf("\n");
            if (nlIdx === -1) break;
            const line = buffer.substring(0, nlIdx);
            buffer = buffer.substring(nlIdx + 1);
            processLine(line);
          }
        }
        buffer += decoder.decode();
        if (buffer.trim()) processLine(buffer);
        if (!finalized) throw new PlateTalkRequestError(plateTalkErrorMessage("stream_incomplete"));
      } catch (err: any) {
        if (!mountedRef.current) return;
        if (controller.signal.aborted && !timedOut) {
          setMessages(prev => prev.map(m => m.id === msgId
            ? { ...m, streaming: false, statusMessage: undefined }
            : m));
          return;
        }
        // Only locally-created errors carry member-facing copy. Unexpected
        // transport exceptions must never expose their raw message.
        const errorContent = err instanceof PlateTalkRequestError
          ? err.message : plateTalkErrorMessage();
        setLastError(errorContent);
        setMessages(prev => {
          const hasContent = prev.find(m => m.id === msgId && m.content);
          if (hasContent) return prev.map(m => m.id === msgId ? { ...m, content: `${m.content}\n\n${errorContent}`, streaming: false, statusMessage: undefined } : m);
          return [...prev.filter(m => m.id !== msgId), {
            id: `e-${requestId}`,
            role: "assistant" as const,
            content: errorContent,
            timestamp: new Date(),
          }];
        });
      } finally {
        window.clearTimeout(timeout);
        // Never await transport cancellation here: a fetch/proxy can leave
        // that promise pending after a terminal frame. The finalized/aborted
        // fences already prevent unread frames from mutating this response.
        if (reader) {
          void reader.cancel().catch(() => {});
          reader.releaseLock();
        }
        if (abortRef.current === controller) abortRef.current = null;
        if (mountedRef.current) {
          setState("idle");
          if (!onPageRef.current) setCompletedWhileAway(true);
          if (controller.signal.aborted) {
            queueRef.current = [];
            setQueueLength(0);
          }
          processNext();
        }
      }
    })();
  }, []);

  const processNext = useCallback(() => {
    if (queueRef.current.length === 0) {
      processingRef.current = false;
      setQueueLength(0);
      return;
    }
    const next = queueRef.current.shift()!;
    setQueueLength(queueRef.current.length);
    processRequest(next);
  }, [processRequest]);

  const sendMessage = useCallback((
    text: string,
    image?: string | null,
    conversationId?: number | null,
    voiceEnabled?: boolean,
    speakFn?: (t: string) => void,
  ) => {
    const req: QueuedRequest = { text, image, conversationId, voiceEnabled, speakFn };

    if (!processingRef.current) {
      processingRef.current = true;
      processRequest(req);
    } else {
      queueRef.current.push(req);
      setQueueLength(queueRef.current.length);
    }
  }, [processRequest]);

  return (
    <PlateTalkContext.Provider value={{
      messages,
      setMessages,
      state,
      activeConversationId,
      setActiveConversationId,
      sendMessage,
      abort,
      completedWhileAway,
      clearCompletedFlag,
      lastError,
      queueLength,
    }}>
      <OnPageTracker setOnPage={setOnPage} />
      {children}
    </PlateTalkContext.Provider>
  );
}

function OnPageTracker({ setOnPage }: { setOnPage: (on: boolean) => void }) {
  const [location] = useLocation();
  useEffect(() => {
    setOnPage(location === "/plate-talk");
  }, [location, setOnPage]);
  return null;
}

export function usePlateTalkStore() {
  const ctx = useContext(PlateTalkContext);
  if (!ctx) throw new Error("usePlateTalkStore must be used within PlateTalkProvider");
  return ctx;
}
